import { useCallback, useEffect, useState } from "react";
import { Bookmark, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import LotGrid from "@/components/LotGrid";
import { getSavedSearches, deleteSavedSearch } from "@/lib/api";

function paramsLabel(params) {
  const parts = [];
  if (params.q) parts.push(`"${params.q}"`);
  if (params.max_hours_remaining) {
    const h = params.max_hours_remaining;
    parts.push(h < 24 ? `entro ${h}h` : `entro ${h / 24} giorn${h / 24 === 1 ? "o" : "i"}`);
  }
  if (params.max_price) parts.push(`max €${params.max_price}`);
  if (params.min_price) parts.push(`min €${params.min_price}`);
  if (params.local_only) parts.push("ritiro locale");
  return parts.join(" · ") || "Tutti i lotti";
}

function PlanBadge({ plan, used, limit }) {
  return (
    <span className="text-[10px] text-white/35 uppercase tracking-[0.18em]">
      {used}/{limit} ricerche · piano {plan}
    </span>
  );
}

export default function SavedSearches({ user }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSavedSearches();
      setEntries(data);
    } catch {
      toast.error("Impossibile caricare le ricerche salvate.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await deleteSavedSearch(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Ricerca eliminata.");
    } catch {
      toast.error("Impossibile eliminare la ricerca.");
    }
  };

  if (!user) {
    return (
      <div className="text-center py-24 text-white/40">
        <Bookmark className="w-10 h-10 mx-auto mb-4 opacity-30" />
        <p className="text-sm">Accedi per vedere le tue ricerche salvate.</p>
      </div>
    );
  }

  const limit = user.plan === "pro" ? 10 : 3;

  return (
    <div className="space-y-12">
      <div className="flex items-center justify-between">
        <PlanBadge plan={user.plan} used={entries.length} limit={limit} />
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40 hover:text-gold transition-colors disabled:opacity-40"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {loading && entries.length === 0 && (
        <div className="flex justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-gold/50" />
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="text-center py-24 text-white/40">
          <Bookmark className="w-10 h-10 mx-auto mb-4 opacity-30" />
          <p className="text-sm">Nessuna ricerca salvata. Usa il pulsante "Salva ricerca" durante una ricerca.</p>
        </div>
      )}

      {entries.map((entry) => (
        <div key={entry.id} className="border-t border-white/8 pt-8">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h3 className="font-display text-xl text-white mb-1">{entry.name}</h3>
              <p className="text-xs text-white/35 uppercase tracking-[0.15em]">{paramsLabel(entry.params)}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {entry.results && (
                <span className="text-[10px] text-white/35 uppercase tracking-widest">
                  {entry.results.filtered_count} lotti
                </span>
              )}
              <button
                type="button"
                onClick={() => handleDelete(entry.id)}
                className="text-white/30 hover:text-red-400 transition-colors"
                title="Elimina"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {entry.error && (
            <p className="text-xs text-red-400/70">Errore nell'esecuzione di questa ricerca.</p>
          )}
          {entry.results && entry.results.items.length > 0 && (
            <LotGrid items={entry.results.items} />
          )}
          {entry.results && entry.results.items.length === 0 && (
            <p className="text-xs text-white/35">Nessun lotto trovato al momento.</p>
          )}
        </div>
      ))}
    </div>
  );
}
